import { formatDistance, startOfDay, subDays, subWeeks, subYears, } from 'date-fns';
const number = (value, { locale, ...options }) => new Intl.NumberFormat(locale, options).format(value);
const formatCurrencyString = (currency, compact = false, { locale, ...options } = {}) => new Intl.NumberFormat(locale, {
    notation: compact ? 'compact' : 'standard',
    style: 'currency',
    currency,
    ...options,
});
const currencySymbol = (currency) => {
    const parts = formatCurrencyString(currency).formatToParts(1);
    return parts.find((part) => part.type === 'currency')?.value ?? currency;
};
// Explicit date/time options replace the default styles so component options remain valid.
const date = (value, { locale, ...options } = { dateStyle: 'medium' }) => new Intl.DateTimeFormat(locale, options).format(new Date(value));
const dateTime = (value, { locale, ...options } = {
    dateStyle: 'full',
    timeStyle: 'long',
}) => new Intl.DateTimeFormat(locale, options).format(new Date(value));
export const isInvalidDate = (value) => Number.isNaN(new Date(value).getTime());
export const formatRelativeTime = (value, full = false) => {
    const valueDate = new Date(value);
    if (isInvalidDate(valueDate))
        return 'Invalid Date';
    const now = new Date();
    const today = startOfDay(now);
    const yesterday = subDays(today, 1);
    const aWeekAgo = subWeeks(today, 1);
    const aYearAgo = subYears(today, 1);
    const timePart = valueDate
        .toLocaleTimeString(undefined, { timeStyle: 'short' })
        .toLocaleLowerCase();
    if (full) {
        return `${valueDate.toLocaleDateString(undefined, { dateStyle: 'long' })} at ${timePart}`;
    }
    if (valueDate > now) {
        return `${valueDate.toLocaleDateString(undefined, { dateStyle: 'medium' })} at ${timePart}`;
    }
    if (valueDate >= yesterday && valueDate < now) {
        return `${valueDate < today ? 'Yesterday' : 'Today'} at ${timePart}`;
    }
    if (valueDate >= aWeekAgo) {
        return `${valueDate.toLocaleDateString(undefined, { weekday: 'long' })} at ${timePart}`;
    }
    if (valueDate >= aYearAgo) {
        return `${valueDate.toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
        })} at ${timePart}`;
    }
    return date(valueDate);
};
const plural = (count, values, locale) => {
    const type = new Intl.PluralRules(locale, { type: 'cardinal' }).select(count);
    return values[type] ?? values.other;
};
export const formatter = {
    number,
    count: (value, compact = false, options = {}) => number(value, {
        notation: compact ? 'compact' : 'standard',
        style: 'decimal',
        ...options,
    }),
    percent: (value, compact = false, options = {}) => number(value, {
        notation: compact ? 'compact' : 'standard',
        minimumSignificantDigits: 2,
        maximumSignificantDigits: 4,
        style: 'percent',
        ...options,
    }),
    // one place, so a column of parts lines up; a part too small to round above zero still reads as some
    share: (value, options = {}) => {
        const oneDecimal = {
            style: 'percent',
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
            ...options,
        };
        return value > 0 && value < 0.0005
            ? `<${number(0.001, oneDecimal)}`
            : number(value, oneDecimal);
    },
    currencySymbol,
    currency: (value, currency, compact = false, options = {}) => formatCurrencyString(currency, compact, options).format(value),
    date,
    dateTime,
    isInvalidDate,
    relativeDateTime: formatRelativeTime,
    relativeTimeFromNow: (value) => formatDistance(value, new Date(), { addSuffix: true }),
    list: {
        and: (items) => new Intl.ListFormat(undefined, { type: 'conjunction' }).format(items),
        or: (items) => new Intl.ListFormat(undefined, { type: 'disjunction' }).format(items),
        unit: (items) => new Intl.ListFormat(undefined, { type: 'unit' }).format(items),
    },
    plural,
};
export const createCurrencyFormatter = (currency) => {
    const currencyFormatter = formatCurrencyString(currency);
    return (value) => currencyFormatter.format(value);
};
export const messagingInDays = (days) => `${days} ${plural(days, { one: 'day', other: 'days' })}`;
//# sourceMappingURL=formatter.js.map